import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, clearAuthToken, getAuthToken, setAuthToken, type ApiError } from "@/lib/api";

export type AuthUser = {
  id: string;
  full_name: string;
  email?: string | null;
  role?: string | null;
  department?: string | null;
  phone_number?: string | null;
  profile_photo?: string | null;
};

type AuthSession = {
  token: string;
};

interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  userRole: string | null;
  userDepartment: string | null;
  loading: boolean;
  signUp: (fullName: string, password: string, email?: string) => Promise<{ error: ApiError | null }>;
  signIn: (login: string, password: string) => Promise<{ error: ApiError | null }>;
  loginAs: (userId: string) => Promise<{ error: ApiError | null }>;
  signOut: () => Promise<void>;
}

type TokenResponse = {
  token: string;
  user: AuthUser;
};

const decodeJwtExpirationMs = (token: string): number | null => {
  try {
    if (typeof window === "undefined" || typeof window.atob !== "function") return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(parts[1].length + ((4 - (parts[1].length % 4)) % 4), "=");
    const parsed = JSON.parse(window.atob(payload)) as { exp?: unknown };
    if (typeof parsed.exp !== "number") return null;
    return parsed.exp * 1000;
  } catch {
    return null;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userDepartment, setUserDepartment] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const clearLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const clearSessionState = useCallback(() => {
    clearAuthToken();
    setSession(null);
    setUser(null);
    setUserRole(null);
    setUserDepartment(null);
  }, []);

  const handleExpiredSession = useCallback(() => {
    clearLogoutTimer();
    clearSessionState();
    navigate("/");
  }, [clearLogoutTimer, clearSessionState, navigate]);

  const scheduleAutoLogout = useCallback((token: string): boolean => {
    clearLogoutTimer();
    const expirationMs = decodeJwtExpirationMs(token);
    if (!expirationMs) return true;
    const delayMs = expirationMs - Date.now();
    if (delayMs <= 0) {
      handleExpiredSession();
      return false;
    }
    logoutTimerRef.current = setTimeout(() => {
      handleExpiredSession();
    }, delayMs);
    return true;
  }, [clearLogoutTimer, handleExpiredSession]);

  useEffect(() => {
    let mounted = true;
    const token = getAuthToken();

    if (!token) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    if (!scheduleAutoLogout(token)) {
      setLoading(false);
      return () => {
        mounted = false;
        clearLogoutTimer();
      };
    }

    const loadMe = async () => {
      try {
        const me = await apiFetch<AuthUser>("/auth/me");
        if (!mounted) return;
        setUser(me);
        setUserRole(me.role ?? null);
        setUserDepartment(me.department ?? null);
        setSession({ token });
      } catch {
        if (!mounted) return;
        clearLogoutTimer();
        clearSessionState();
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadMe();

    return () => {
      mounted = false;
      clearLogoutTimer();
    };
  }, [clearLogoutTimer, clearSessionState, scheduleAutoLogout]);

  const signUp = async (fullName: string, password: string, email?: string) => {
    try {
      const payload: Record<string, string> = { fullName, password };
      if (email && email.trim()) {
        payload.email = email.trim();
      }
      const response = await apiFetch<TokenResponse>("/auth/register", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setAuthToken(response.token);
      scheduleAutoLogout(response.token);
      setSession({ token: response.token });
      setUser(response.user);
      setUserRole(response.user.role ?? null);
      setUserDepartment(response.user.department ?? null);
      return { error: null };
    } catch (error) {
      return { error: error as ApiError };
    }
  };

  const signIn = async (login: string, password: string) => {
    try {
      const response = await apiFetch<TokenResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ login, password }),
      });
      setAuthToken(response.token);
      scheduleAutoLogout(response.token);
      setSession({ token: response.token });
      setUser(response.user);
      setUserRole(response.user.role ?? null);
      setUserDepartment(response.user.department ?? null);
      navigate("/dashboard");
      return { error: null };
    } catch (error) {
      return { error: error as ApiError };
    }
  };

  const signOut = async () => {
    clearLogoutTimer();
    clearSessionState();
    navigate("/");
  };

  const loginAs = async (userId: string) => {
    try {
      const response = await apiFetch<TokenResponse>(`/auth/login-as/${userId}`, {
        method: "POST",
      });
      setAuthToken(response.token);
      scheduleAutoLogout(response.token);
      setSession({ token: response.token });
      setUser(response.user);
      setUserRole(response.user.role ?? null);
      setUserDepartment(response.user.department ?? null);
      navigate("/dashboard");
      return { error: null };
    } catch (error) {
      return { error: error as ApiError };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userRole,
        userDepartment,
        loading,
        signUp,
        signIn,
        loginAs,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
